import { useGetPatientById } from "@elation/api-client/src/stitch-web";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import { Flexbox, HorizontalFlexbox } from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@src/components/ui/dropdown-menu";
import { navigation } from "@src/navigation";
import { signOut } from "@src/okta/tokenManager";
import { differenceInYears } from "date-fns/differenceInYears";
import { ArrowLeft, FileText, LogOut, Menu, Settings, X } from "lucide-react";
import { useNavigate, useParams } from "react-router";

export interface HeaderProps {
  onMenuToggle: () => void;
  isChromeExtension: boolean;
  showMenuToggle?: boolean;
  onClose?: () => void;
}

export interface EmbeddedHeaderProps {
  onClose?: () => void;
  patientId: number | null;
}

/**
 * Header used when the app is embedded in the Chrome extension or consumed as
 * a package. Keeps the surface compact and exposes a close/settings menu.
 */
export const EmbeddedHeader = ({ onClose }: EmbeddedHeaderProps) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate(navigation.login());
  };

  return (
    <HorizontalFlexbox
      as="header"
      alignItems="center"
      justifyContent="space-between"
      gap="md"
      className="h-12 border-b border-border bg-[hsl(var(--stitch-blue))] px-4 text-white"
    >
      <HorizontalFlexbox alignItems="center" gap="sm">
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-7 rounded-md bg-white/10"
        >
          <FileText className="size-4" />
        </Flexbox>
        <Text as="span" sizing="sm" fontWeight={600}>
          Value-Based Visit Assistant
        </Text>
      </HorizontalFlexbox>
      <HorizontalFlexbox alignItems="center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
            >
              <Settings />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Settings</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 size-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close"
          className="text-white hover:bg-white/10"
        >
          <X />
        </Button>
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
};

/**
 * Primary dashboard header. Shows a menu toggle, current patient banner (if
 * we're on a patient route), and logout action.
 */
export const Header = ({
  onMenuToggle,
  isChromeExtension,
  showMenuToggle = true,
  onClose,
}: HeaderProps) => {
  const navigate = useNavigate();

  const params = useParams();
  const patientId = params.patientId;

  const { data: patientData } = useGetPatientById(Number(patientId), {
    query: {
      enabled: !!patientId,
    },
  });

  if (isChromeExtension) {
    return <EmbeddedHeader onClose={onClose} patientId={Number(patientId)} />;
  }

  return (
    <HorizontalFlexbox
      as="header"
      alignItems="center"
      justifyContent="space-between"
      gap="md"
      className="sticky top-0 z-20 h-16 shrink-0 border-b border-border bg-background/90 px-4 backdrop-blur-md md:px-6"
    >
      <HorizontalFlexbox alignItems="center" gap="sm">
        {showMenuToggle ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            aria-label="Toggle sidebar"
          >
            <Menu />
          </Button>
        ) : null}
        {patientId ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(navigation.patients())}
            aria-label="Back to patient list"
            className="gap-1"
          >
            <ArrowLeft className="size-4" />
            <span className="hidden sm:inline">Patients</span>
          </Button>
        ) : null}
        {patientData?.data ? (
          <HorizontalFlexbox alignItems="center" gap="sm">
            <HeaderText tag="h1" className="text-lg">
              {patientData.data.firstName} {patientData.data.lastName}
            </HeaderText>
            <span aria-hidden className="hidden h-5 w-px bg-border sm:block" />
            <Text
              as="span"
              sizing="sm"
              appearance="subdued"
              className="hidden sm:inline"
            >
              {differenceInYears(new Date(), new Date(patientData.data.dob))}
              {patientData.data.sex.charAt(0).toUpperCase()}
            </Text>
          </HorizontalFlexbox>
        ) : (
          <HeaderText tag="h1" className="text-lg">
            Stitch
          </HeaderText>
        )}
      </HorizontalFlexbox>
      <HorizontalFlexbox alignItems="center" gap="xs">
        <Button size="sm" onClick={() => navigate(navigation.logout())}>
          <LogOut className="mr-1 size-4" />
          Logout
        </Button>
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
};
